/**
 * OfficerPicker — the one picker, mounted once, for whichever cell is open.
 *
 * Exactly one of these exists at a time. On a 40-post site the grid is ~840
 * cells; mounting a picker per cell is what makes a spreadsheet-replacement
 * grid feel slow, so a cell is a plain button and this opens against it.
 *
 * The list comes from the server already in assignment order: officers
 * assigned to the job first, then everyone else in scope, then anyone with a
 * qualification gap or a clash, each carrying the reason on the row. No rate
 * appears here, for anybody - not a pay rate and not a bill rate. A picker is
 * a list of people, and a rate on it would be a pay report.
 */
import React, { useEffect, useRef, useState } from 'react';
import { schedulingAPI } from '../../lib/api';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { AlertTriangle, Clock, Search, UserX, UserPlus, Check, X } from 'lucide-react';

const hhmm = (s) => (s || '').slice(0, 5);
const TIER_LABELS = {
  1: 'On this job or client',
  2: 'Same manager or supervisory group',
  3: 'All active officers',
};

export function OfficerPicker({ cell, jobId, onChoose, onOpenPost, onClear, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [empty, setEmpty] = useState(null);
  const [addedIds, setAddedIds] = useState(() => new Set());
  const [addingId, setAddingId] = useState(null);
  const boxRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => { searchRef.current?.focus(); }, []);

  const addRegular = async (r, e) => {
    e.stopPropagation();
    setAddingId(r.employee_id);
    try {
      await schedulingAPI.addRegular(jobId, { employee_id: r.employee_id });
      setAddedIds((prev) => new Set(prev).add(r.employee_id));
      toast.success(`${r.display_last_first} added to this job's regulars`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add to regulars');
    } finally {
      setAddingId(null);
    }
  };

  useEffect(() => {
    let live = true;
    setLoading(true);
    const t = setTimeout(() => {
      schedulingAPI.eligibleEmployees({
        job_id: jobId,
        job_post_id: cell.postId,
        shift_date: cell.date,
        time_in: cell.timeIn || undefined,
        time_out: cell.timeOut || undefined,
        next_day: cell.nextDay || false,
        search: search || undefined,
      }).then(({ data }) => {
        if (!live) return;
        setRows(data.employees || []);
        setEmpty(data.empty_reason || null);
      }).catch(() => live && setRows([]))
        .finally(() => live && setLoading(false));
    }, search ? 220 : 0);
    return () => { live = false; clearTimeout(t); };
  }, [jobId, cell.postId, cell.date, cell.timeIn, cell.timeOut, cell.nextDay, search]);

  useEffect(() => {
    const away = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) onClose();
    };
    const esc = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc, true);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc, true);
    };
  }, [onClose]);

  return (
    <div ref={boxRef} data-testid="officer-picker"
      className="w-80 max-w-[92vw] max-h-[min(85vh,460px)] flex flex-col rounded-xl border border-line bg-surface shadow-xl
                 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
      <div className="px-3 pt-3 pb-2 border-b border-line-subtle">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-fg truncate">{cell.postName}</p>
            <p className="text-xs text-fg-muted tabular-nums">
              {cell.date} · {hhmm(cell.timeIn) || '--:--'}–{hhmm(cell.timeOut) || '--:--'}
              {cell.nextDay ? ' (+1d)' : ''}
            </p>
          </div>
          <button onClick={onClose} data-testid="officer-picker-close"
            aria-label="Close" className="text-fg-muted hover:text-fg p-1 -m-1">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="relative mt-2">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-muted" />
          <Input ref={searchRef} value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Type a name or number" data-testid="officer-picker-search"
            className="h-9 pl-8 text-sm" />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto" role="listbox">
        {loading ? (
          <p className="text-xs text-fg-muted px-3 py-4" data-testid="officer-picker-loading">
            Looking…
          </p>
        ) : !rows.length ? (
          <p className="text-xs text-fg-muted px-3 py-4" data-testid="officer-picker-empty">
            {search.trim()
              ? 'Nobody matches that.'
              : 'No regulars or related officers yet — type a name or number to search everyone.'}
          </p>
        ) : rows.map((r, i) => {
          const showHeader = i === 0 || rows[i - 1].tier !== r.tier;
          const isRegular = r.is_assigned_to_job || addedIds.has(r.employee_id);
          return (
          <React.Fragment key={r.employee_id}>
            {showHeader && (
              <div data-testid={`officer-tier-header-${r.tier}`}
                className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide
                           text-fg-muted bg-surface-sunken/60 sticky top-0">
                {TIER_LABELS[r.tier] || 'Officers'}
              </div>
            )}
            <button type="button" role="option"
              data-testid={`officer-option-${r.employee_id}`}
              onClick={() => onChoose(r)}
              className="w-full text-left px-3 py-2 hover:bg-surface-sunken transition-colors
                         border-b border-line-subtle last:border-0">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-fg truncate">{r.display_last_first}</span>
                <span className="text-xs text-fg-muted tabular-nums whitespace-nowrap
                                 flex items-center gap-1">
                  <Clock className="h-3 w-3" />{r.scheduled_hours_this_week}h
                </span>
              </div>
              {/* Reasons — stated as facts, never a conclusion */}
              {r.reasons?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1"
                     data-testid={`officer-reasons-${r.employee_id}`}>
                  {r.reasons.map((reason, ri) => (
                    <span key={ri}
                      className="text-[10px] text-fg-muted bg-surface-sunken rounded px-1.5 py-0.5">
                      {reason}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                {r.is_primary_assignment && (
                  <Badge variant="outline" className="text-[10px] py-0">Primary</Badge>
                )}
                {!r.is_primary_assignment && isRegular && (
                  <Badge variant="outline" className="text-[10px] py-0">On this job</Badge>
                )}
                {r.conflict_status === 'double_booked' && (
                  <span className="text-[11px] text-warning flex items-center gap-1"
                        data-testid={`officer-conflict-${r.employee_id}`}>
                    <AlertTriangle className="h-3 w-3" />
                    Already on {r.conflicts?.[0]?.job_number || 'another job'}
                    {' '}{r.conflicts?.[0]?.window}
                  </span>
                )}
                {r.qualification_status !== 'ok' && (
                  <span className="text-[11px] text-warning flex items-center gap-1"
                        data-testid={`officer-qual-${r.employee_id}`}>
                    <AlertTriangle className="h-3 w-3" />
                    {r.qualification_issues?.join(', ')} {r.qualification_status}
                  </span>
                )}
                {!isRegular && (
                  <button type="button"
                    data-testid={`officer-add-regular-${r.employee_id}`}
                    onClick={(e) => addRegular(r, e)}
                    disabled={addingId === r.employee_id}
                    className="ml-auto text-[11px] text-brand flex items-center gap-1
                               hover:underline disabled:opacity-50">
                    <UserPlus className="h-3 w-3" />
                    {addingId === r.employee_id ? 'Adding…' : 'Add to regulars'}
                  </button>
                )}
                {isRegular && addedIds.has(r.employee_id) && (
                  <span className="ml-auto text-[11px] text-success flex items-center gap-1">
                    <Check className="h-3 w-3" /> Added
                  </span>
                )}
              </div>
            </button>
          </React.Fragment>
          );
        })}
      </div>

      <div className="flex items-center gap-2 px-3 py-2 border-t border-line-subtle
                      bg-surface-sunken">
        <Button variant="outline" size="sm" onClick={onOpenPost}
          data-testid="officer-picker-open-post">
          <UserX className="h-3.5 w-3.5 mr-1.5" /> Leave open
        </Button>
        {cell.shiftId && (
          <Button variant="ghost" size="sm" onClick={onClear}
            data-testid="officer-picker-remove-shift">
            Remove shift
          </Button>
        )}
      </div>
    </div>
  );
}
